import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';

const STORAGE_KEY = 'lanura_dark_mode_enabled';

interface DarkModeContextType {
  enabled: boolean;
  setEnabled: (enabled: boolean) => void;
  toggle: () => void;
}

const DarkModeContext = createContext<DarkModeContextType | undefined>(undefined);

export const DarkModeProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [enabled, setEnabledState] = useState<boolean>(() => {
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === null ? false : stored === 'true';
  });

  const setEnabled = (value: boolean) => {
    setEnabledState(value);
    localStorage.setItem(STORAGE_KEY, value.toString());
    // Dispatch custom event to notify other components
    window.dispatchEvent(new CustomEvent('darkModeChanged', { detail: { enabled: value } }));
    // Update body class for global styling
    if (value) {
      document.body.classList.add('dark-mode-enabled');
    } else {
      document.body.classList.remove('dark-mode-enabled');
    }
  };

  const toggle = () => setEnabled(!enabled);

  // Initialize body class on mount and when enabled changes
  useEffect(() => {
    if (enabled) {
      document.body.classList.add('dark-mode-enabled');
    } else {
      document.body.classList.remove('dark-mode-enabled');
    }
  }, [enabled]);

  // Listen for storage changes (e.g., from other tabs)
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        const newValue = e.newValue === 'true';
        setEnabledState(newValue);
        if (newValue) {
          document.body.classList.add('dark-mode-enabled');
        } else {
          document.body.classList.remove('dark-mode-enabled');
        }
      }
    };

    const handleCustomEvent = (e: Event) => {
      const customEvent = e as CustomEvent;
      if (customEvent.detail?.enabled !== undefined) {
        setEnabledState(customEvent.detail.enabled);
        if (customEvent.detail.enabled) {
          document.body.classList.add('dark-mode-enabled');
        } else {
          document.body.classList.remove('dark-mode-enabled');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('darkModeChanged', handleCustomEvent);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('darkModeChanged', handleCustomEvent);
    };
  }, []);

  return (
    <DarkModeContext.Provider value={{ enabled, setEnabled, toggle }}>
      {children}
    </DarkModeContext.Provider>
  );
};

export const useDarkMode = () => {
  const context = useContext(DarkModeContext);
  if (context === undefined) {
    throw new Error('useDarkMode must be used within a DarkModeProvider');
  }
  return context;
};
